<script setup lang="ts">
  defineOptions({ inheritAttrs: false, name: 'CPSelect' })

  import { computed, type PropType } from 'vue'

  import { HLSelect } from '@platform-ui/highrise'

  import type { CPHighriseSelectOption, CPHighriseSelectValue, CPSelectOption, CPSelectValue } from './types/select.types'

  // Props definition with proper types
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number, Boolean, Array, Object],
      default: undefined,
    },
    value: {
      type: [String, Number, Boolean, Array, Object] as PropType<CPSelectValue>,
      default: undefined,
    },
    options: {
      type: Array as PropType<CPSelectOption[]>,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Please select',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as () => 'xs' | 'sm' | 'md' | 'lg',
      default: 'md',
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: undefined,
    },
    customClass: {
      type: [String, Array, Object],
      default: undefined,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
  })

  // Emits
  // CPSelect historically accepts consumers with value-specific handlers.
  // Keep that public event boundary permissive while adapting v4 internally.
  const emit = defineEmits(['update:value', 'focus', 'blur'])

  const highriseOptions = computed(() => props.options as unknown as CPHighriseSelectOption[])
  // Highrise accepts null at runtime to clear the Naive select, but its v4 Value type omits null.
  const selectedValue = computed(
    () => (props.value !== undefined ? props.value : props.modelValue) as unknown as CPHighriseSelectValue | undefined
  )

  // Event handlers
  const handleUpdateValue = (...args: unknown[]) => {
    emit('update:value', args[0], args[1])
  }

  const handleFocus = (event: FocusEvent) => {
    emit('focus', event)
  }

  const handleBlur = (event: FocusEvent) => {
    emit('blur', event)
  }
</script>

<template>
  <div class="cp-select-wrapper" :class="{ 'w-full': props.fullWidth }">
    <HLSelect
      v-bind="$attrs"
      :id="props.id"
      :value="selectedValue"
      :options="highriseOptions"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :size="props.size"
      :filterable="props.filterable"
      :clearable="props.clearable"
      :multiple="props.multiple"
      :show-arrow="props.showArrow"
      :title="props.title"
      @update:value="handleUpdateValue"
      @focus="handleFocus"
      @blur="handleBlur"
    />
  </div>
</template>

<style scoped>
  .cp-select-wrapper {
    position: relative;
    display: inline-block;
  }

  .cp-select-wrapper :deep(.n-base-selection) {
    transition: all 0.2s ease;
  }

  .cp-select-wrapper :deep(.n-base-selection--disabled) {
    cursor: not-allowed;
  }

  .cp-select-wrapper :deep(.n-base-selection-label) {
    font-weight: 500;
  }

  .cp-select-wrapper :deep(.n-base-selection-placeholder) {
    color: var(--cp-text-secondary, #6b7280);
  }

  /* Size overrides */
  .cp-select-wrapper.cp-select--xs :deep(.n-base-selection) {
    min-height: 28px;
    font-size: 12px;
    padding: 0 8px;
  }

  .cp-select-wrapper.cp-select--sm :deep(.n-base-selection) {
    min-height: 32px;
    font-size: 14px;
    padding: 0 12px;
  }

  .cp-select-wrapper.cp-select--md :deep(.n-base-selection) {
    min-height: 36px;
    font-size: 14px;
    padding: 0 12px;
  }

  .cp-select-wrapper.cp-select--lg :deep(.n-base-selection) {
    min-height: 40px;
    font-size: 16px;
    padding: 0 16px;
  }

  /* Filterable state */
  .cp-select-wrapper :deep(.n-base-selection--filterable) {
    cursor: text;
  }

  /* Multiple selection */
  .cp-select-wrapper :deep(.n-base-selection--multiple) {
    min-height: auto;
    padding: 4px 8px;
  }

  .cp-select-wrapper :deep(.n-base-selection--multiple .n-base-selection-label) {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  /* Arrow customization */
  .cp-select-wrapper :deep(.n-base-selection-arrow) {
    transition: transform 0.2s ease;
  }

  .cp-select-wrapper :deep(.n-base-selection--focused .n-base-selection-arrow) {
    transform: rotate(180deg);
  }

  /*
   * Selected-option text/check colours are an intentional deviation from
   * HighRise defaults:
   *   - optionTextColorActive defaults to var(--gray-700) in HighRise
   *   - optionCheckColor      defaults to var(--primary-700) in HighRise
   * Product wants the selected option to pick up the brand primary, so we
   * tint both here. (Focus ring, border, and menu-active tokens are already
   * brand-aware via the --primary-* bridge in useTheme.ts + theme.css.)
   */
  .cp-select-wrapper :deep(.n-base-select-option--selected) {
    --n-option-text-color-active: var(--cp-primary-600);
    color: var(--cp-primary-600);
  }

  .cp-select-wrapper :deep(.n-base-select-option--selected .n-check) {
    color: var(--cp-primary-500);
  }
</style>

<!--
  HighRise select overrides — unscoped because HLSelect teleports its
  popup menu into document.body. <style scoped> and :deep() cannot reach
  teleported nodes. The trigger surface (.hr-base-selection / .n-base-selection)
  also lives outside the wrapper element when slot content is hoisted, so
  the same global hook is used to keep the trigger and menu in sync.

  Note: .hr-base-select-menu uses --n-menu-color for its background (not
  --n-color like popovers). Both are set so the popup adopts the themed
  background token reliably.

  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Floating select menu — matches the popover/dropdown shell pattern */
  .hr-base-select-menu {
    --n-color: var(--cp-background-500) !important;
    --n-menu-color: var(--cp-background-500) !important;
    --n-text-color: var(--cp-brand-font) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    border: 1px solid var(--cp-border-500) !important;
  }

  /* Option rows — background, text, hover and selected states */
  .hr-base-select-menu .hr-base-select-option {
    color: var(--cp-brand-font) !important;
    --n-option-text-color: var(--cp-brand-font) !important;
    --n-option-text-color-active: var(--cp-brand-font) !important;
    --n-option-text-color-pressed: var(--cp-brand-font) !important;
    --n-option-color-pending: var(--cp-hover) !important;
    --n-option-color-active: var(--cp-hover) !important;
  }

  /* Option body base background */
  .hr-base-select-menu .hr-base-select-option:not(.hr-base-select-option--disabled) {
    background-color: var(--cp-background-500) !important;
    color: var(--cp-brand-font) !important;
  }

  /* HighRise select options use ::before for the hover/pending overlay.
     Override both the CSS variable AND the pseudo-element directly. */
  .hr-base-select-menu .hr-base-select-option::before {
    background-color: transparent !important;
  }

  .hr-base-select-menu .hr-base-select-option.hr-base-select-option--pending::before,
  .hr-base-select-menu .hr-base-select-option:hover::before {
    background-color: var(--cp-hover) !important;
  }

  /* Fallback: direct background for pending/hover state */
  .hr-base-select-menu .hr-base-select-option.hr-base-select-option--pending,
  .hr-base-select-menu .hr-base-select-option:hover {
    background-color: var(--cp-hover) !important;
  }

  /* Group headers in select menus */
  .hr-base-select-menu .hr-base-select-group-header,
  .hr-select__menu-container .hr-base-select-group-header {
    background: var(--cp-surface-500) !important;
    color: var(--cp-brand-font) !important;
  }

  /*
   * Select trigger surface.
   *
   * CPSelect renders with .n-base-selection (NaiveUI prefix) AND
   * .hr-base-selection (HighRise prefix) depending on the build. Both
   * are listed so the trigger always picks up the themed background,
   * border, focus colours, and disabled state.
   */
  .hr-base-selection,
  .n-base-selection {
    --n-color: var(--cp-background-500) !important;
    --n-color-focus: var(--cp-background-500) !important;
    /* Disabled: subtle darkening of the background — avoids harsh brown in warm themes */
    --n-color-disabled: var(--cp-background-600) !important;
    /* Use the user's explicit font-color token so white/dark themes apply correctly */
    --n-text-color: var(--cp-brand-font) !important;
    --n-text-color-disabled: var(--cp-font-400) !important;
    --n-placeholder-color: color-mix(in oklab, var(--cp-brand-font) 40%, transparent) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    --n-border-hover: 1px solid var(--cp-primary-500) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
    --n-border-disabled: 1px solid var(--cp-border-500) !important;
    --n-caret-color: var(--cp-primary-500) !important;
  }

  /* Disabled text color overrides for inner elements */
  .hr-base-selection.n-base-selection--disabled .n-base-selection-label,
  .n-base-selection.n-base-selection--disabled .n-base-selection-label,
  .hr-base-selection.n-base-selection--disabled input,
  .n-base-selection.n-base-selection--disabled input {
    color: var(--cp-font-400) !important;
    -webkit-text-fill-color: var(--cp-font-400) !important;
  }

  /* FOCUS-RING
   * HighRise's shared input theme hard-codes the focus-ring glow to `#D1E0FF`
   * (see highrise481.mjs → commonInputStyles.boxShadowFocus). Override with
   * --cp-primary-100 so the glow always matches the active brand. */
  .hr-base-selection {
    --n-box-shadow-focus: 0px 0px 0px 4px var(--cp-primary-100), var(--shadow-xs) !important;
  }

  /* Disabled trigger — slightly darkened background instead of --cp-surface-600
     which reads as a harsh brown in warm/dark themes */
  .hr-base-selection[disabled] {
    --n-color-disabled: var(--cp-background-600) !important;
  }
</style>
